import {Continent} from './Continent';
import {CountryData} from './Country';
import {Ethnicities} from './Ethnicities';
import {GenderBits, MatchGender} from './Gender';
import {GoalBit} from './Goal';
import {Interests} from './Interests';
import {
  isArrayOptionalEqual,
  isArraySimpleEqual,
  packBits,
  unpackBits,
} from './Model';
import {Nationalities} from './Nationalities';
import {Notifications} from './Notifications';
import {ReviewStatus} from './ReviewStatus';
import {SupportedLang, SupportedLanguagesEnd} from './SupportedLang';
import {
  UserDrinking,
  UserEducationLevel,
  UserExercise,
  UserKids,
  UserMaritalStatus,
  UserPets,
  UserReligion,
  UserSexOrientation,
  UserSmoking,
  UserStarSign,
  UserValues,
} from './UserDetailed';

export type VerificationStatus = 'NONE' | 'PENDING' | 'DONE' | 'ERROR';

export enum AmbassadorStatus {
  NONE = 0,
  PROMPT = 1,
  SUBMITTED,
  APPROVED,
  APPROVE_REVIEWED,
  DECLINED,
  DECLINE_REVIEWED,
}

export enum MatchesFilter {
  ACTIVE = 0,
  UNREAD = 1,
  ALL = 2,
}

export enum FilterFlags {
  AGE_EXT = 1 << 0,
  LANGS_EXT = 1 << 1,
  COUNTRIES_EXT = 1 << 2,
  GOALS_EXT = 1 << 3,
  ETHNICITIES_EXT = 1 << 4,
  NATIONALITIES_EXT = 1 << 5,
  INTERESTS_EXT = 1 << 6,
  HEIGHT_EXT = 1 << 7,
  WEIGHT_EXT = 1 << 8,
  SEX_ORIENTATION_EXT = 1 << 9,
  STAR_SIGN_EXT = 1 << 10,
  EXERCISE_EXT = 1 << 11,
  EDUCATION_LEVEL_EXT = 1 << 12,
  MARITAL_STATUS_EXT = 1 << 13,
  KIDS_EXT = 1 << 14,
  DRINKING_EXT = 1 << 15,
  SMOKING_EXT = 1 << 16,
  PETS_EXT = 1 << 17,
  RELIGION_EXT = 1 << 18,
  VALUES_EXT = 1 << 19,
  MATCH_GENDERS_EXT = 1 << 20,
  CONTINENTS_EXT = 1 << 21,
}

export type FilterMoreAboutSettings = {
  filter_drinking?: UserDrinking[];
  filter_education_level?: UserEducationLevel[];
  filter_exercise?: UserExercise[];
  filter_kids?: UserKids[];
  filter_marital_status?: UserMaritalStatus[];
  filter_pets?: UserPets[];
  filter_religion?: UserReligion[];
  filter_sex_orientation?: UserSexOrientation[];
  filter_smoking?: UserSmoking[];
  filter_star_sign?: UserStarSign[];
  filter_values?: UserValues[];
};

export type Settings = {
  age_visible: boolean;
  ambassador_status?: AmbassadorStatus;
  avatar_moderated?: boolean;
  best_avatar: boolean;
  birthday_modified: boolean;
  city_sync_at?: number;
  discover_near: boolean;
  distance_visible: boolean;
  filter_continents: Continent[];
  filter_countries?: CountryData[];
  filter_ethnicities?: Ethnicities[];
  filter_flags: number;
  filter_goals?: GoalBit;
  filter_height_from_cm: number;
  filter_height_to_cm: number;
  filter_interests?: Interests[];
  filter_langs: number[];
  filter_nationalities?: Nationalities[];
  filter_verified_only: boolean;
  filter_weight_from_kg: number;
  filter_weight_to_kg: number;
  gender_modified: boolean;
  incognito: boolean;
  incomplete: boolean;
  mandatory_photo_verification: boolean;
  match_age_from: number;
  match_age_to: number;
  match_gender: MatchGender;
  match_genders: GenderBits;
  matches_filter: MatchesFilter;
  name_modified: boolean;
  notifications: Notifications;
  online_visible: boolean;
  review_status_android: ReviewStatus;
  review_status_android_at?: number;
  review_status_ios: ReviewStatus;
  review_status_ios_at?: number;
  tr_matches: boolean;
  unconfirmed?: true;
  verification_photo_url?: string;
  verification_status: VerificationStatus;
} & FilterMoreAboutSettings;

export const defaultSettings: Settings = {
  age_visible: true,
  best_avatar: true,
  birthday_modified: false,
  discover_near: false,
  distance_visible: true,
  filter_continents: [],
  filter_flags: 0,
  filter_height_from_cm: 130,
  filter_height_to_cm: 229,
  filter_langs: [0, 0],
  filter_verified_only: false,
  filter_weight_from_kg: 40,
  filter_weight_to_kg: 219,
  gender_modified: false,
  incognito: false,
  incomplete: false,
  mandatory_photo_verification: false,
  match_age_from: 18,
  match_age_to: 60,
  match_gender: 'U',
  match_genders: 0,
  matches_filter: MatchesFilter.ACTIVE,
  name_modified: false,
  notifications: ~0 ^ Notifications.Visit_Remote,
  online_visible: false,
  review_status_android: 'NONE',
  review_status_ios: 'NONE',
  tr_matches: true,
  verification_status: 'NONE',
};

export function packFilterLanguages(languages: SupportedLang[]): number[] {
  return packBits(languages, 2);
}

export function unpackFilterLanguages(
  languages?: SupportedLang[],
): SupportedLang[] {
  if (languages) {
    const unpacked = unpackBits(languages);
    const filtered = unpacked.filter((value) => value <= SupportedLanguagesEnd);
    if (filtered.length > 0) {
      return filtered;
    }
  }
  return [];
}

export function isFilterCountriesEqual(
  one?: CountryData[],
  two?: CountryData[],
): boolean {
  if (!one && !two) return true;

  const sortedOne = one?.map(({code}) => code).sort() || [];
  const sortedTwo = two?.map(({code}) => code).sort() || [];

  return isArraySimpleEqual(sortedOne, sortedTwo);
}

export function areDiscoverySettingsEqual(a: Settings, b: Settings): boolean {
  return (
    a.match_age_from === b.match_age_from &&
    a.match_age_to === b.match_age_to &&
    a.match_gender === b.match_gender &&
    a.match_genders === b.match_genders &&
    a.discover_near === b.discover_near &&
    a.filter_verified_only === b.filter_verified_only &&
    a.filter_flags === b.filter_flags &&
    isArrayOptionalEqual(a.filter_langs, b.filter_langs) &&
    a.filter_goals === b.filter_goals &&
    isFilterCountriesEqual(a.filter_countries, b.filter_countries) &&
    isArraySimpleEqual(a.filter_continents, b.filter_continents) &&
    isArrayOptionalEqual(a.filter_ethnicities, b.filter_ethnicities) &&
    isArrayOptionalEqual(a.filter_nationalities, b.filter_nationalities) &&
    isArrayOptionalEqual(a.filter_interests, b.filter_interests) &&
    a.filter_weight_from_kg === b.filter_weight_from_kg &&
    a.filter_weight_to_kg === b.filter_weight_to_kg &&
    a.filter_height_from_cm === b.filter_height_from_cm &&
    a.filter_height_to_cm === b.filter_height_to_cm &&
    isArrayOptionalEqual(a.filter_star_sign, b.filter_star_sign) &&
    isArrayOptionalEqual(a.filter_sex_orientation, b.filter_sex_orientation) &&
    isArrayOptionalEqual(a.filter_education_level, b.filter_education_level) &&
    isArrayOptionalEqual(a.filter_marital_status, b.filter_marital_status) &&
    isArrayOptionalEqual(a.filter_exercise, b.filter_exercise) &&
    isArrayOptionalEqual(a.filter_kids, b.filter_kids) &&
    isArrayOptionalEqual(a.filter_drinking, b.filter_drinking) &&
    isArrayOptionalEqual(a.filter_smoking, b.filter_smoking) &&
    isArrayOptionalEqual(a.filter_religion, b.filter_religion) &&
    isArrayOptionalEqual(a.filter_pets, b.filter_pets) &&
    isArrayOptionalEqual(a.filter_values, b.filter_values)
  );
}
